import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    viewChild
} from "@angular/core";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {SelectorType} from '../../../../inputs/selector-control/selector';
import {SelectorControlComponent} from '../../../../inputs/selector-control/selector-control.component';
import {TouchableDirective} from "../../../../ui-elements/touchable.directive";
import {MenuModel} from '../../../../menu/menu.model';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ImageCacheDirective} from '../../../../ui-elements/image/image-cache.directive';
import {CurrencyPipe} from '../../../../utils/currency.pipe';
import {MenuItemImageOverlay} from '../../../../menu/menu-entity.model';
import {ActivatableDirective} from '../../../../ui-elements/activatable.directive';

export enum OrderItemOptionLayout {
    HORIZONTAL,
    VERTICAL,
}

export enum OrderItemOptionSize {
    DESKTOP,
    MOBILE,
}

@Component({
    selector: "woa-order-item-option-control",
    templateUrl: "./order-item-option-control.component.html",
    styleUrls: ["./order-item-option-control.component.scss"],
    host: {
        "[class.compact]": "compact()",
        "[class.aligned]": "aligned()",
        "[class.with-image]": "showImage()",
        "[class.with-control]": "showControl()",
        "[class.unavailable]": "isUnavailable()",
        "[class.horizontal]": "isHorizontalLayout()",
        "[class.vertical]": "isVerticalLayout()",
        "[class.desktop-size]": "idDesktopSize()",
        "[class.mobile-size]": "isMobileSize()",
        "[class.with-content]": "hasContent()"
    },
    imports: [
        CurrencyPipe,
        ImageCacheDirective,
        NgClass,
        SelectorControlComponent,
        TouchableDirective,
        TranslateModule,
        NgOptimizedImage,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemOptionControlComponent {
    public menuModel: MenuModel = inject(MenuModel);

    MenuItemImageOverlay = MenuItemImageOverlay;

    public readonly title: InputSignal<string> = input.required<string>();

    public readonly image: InputSignal<FedeltaImageData> = input<FedeltaImageData>();
    public readonly imageOverlay: InputSignal<MenuItemImageOverlay> = input<MenuItemImageOverlay>();
    public readonly controlType: InputSignal<SelectorType> = input<SelectorType>(SelectorType.RADIO);
    public readonly layout: InputSignal<OrderItemOptionLayout> = input<OrderItemOptionLayout>(OrderItemOptionLayout.HORIZONTAL);
    public readonly size: InputSignal<OrderItemOptionSize> = input<OrderItemOptionSize>(OrderItemOptionSize.DESKTOP);
    public readonly compact: InputSignal<boolean> = input<boolean>(false);
    public readonly aligned: InputSignal<boolean> = input<boolean>(false);
    public readonly unavailable: InputSignal<boolean> = input<boolean>(false);
    public readonly outOfStock: InputSignal<boolean> = input<boolean>(false);
    public readonly atLimit: InputSignal<boolean> = input<boolean>(false);
    public readonly price: InputSignal<number> = input<number>(0);
    public readonly guestPrice: InputSignal<number> = input<number>(0);
    public readonly showImage: InputSignal<boolean> = input<boolean>(true);
    public readonly showControl: InputSignal<boolean> = input<boolean>(true);
    public readonly disableControl: InputSignal<boolean> = input<boolean>(false);
    public readonly hidePrice: InputSignal<boolean> = input<boolean>(false);
    public readonly showEmptyPrice: InputSignal<boolean> = input<boolean>(false);
    public readonly showPriceSign: InputSignal<boolean> = input<boolean>(true);
    public readonly selected: InputSignal<boolean> = input<boolean>(false);

    public onSelect: OutputEmitterRef<void> = output<void>();

    public touchable: Signal<TouchableDirective> = viewChild(TouchableDirective);
    public content: Signal<ElementRef> = viewChild.required("content");

    public readonly isUnavailable: Signal<boolean> = computed<boolean>(() => this.unavailable() || this.outOfStock() || this.atLimit());
    public readonly isHorizontalLayout: Signal<boolean> = computed<boolean>(() => this.layout() === OrderItemOptionLayout.HORIZONTAL);
    public readonly isVerticalLayout: Signal<boolean> = computed<boolean>(() => this.layout() === OrderItemOptionLayout.VERTICAL);
    public readonly idDesktopSize: Signal<boolean> = computed<boolean>(() => this.size() === OrderItemOptionSize.DESKTOP);
    public readonly isMobileSize: Signal<boolean> = computed<boolean>(() => this.size() === OrderItemOptionSize.MOBILE);
    public readonly hasContent: Signal<boolean> = computed<boolean>(() => this.content()?.nativeElement.children.length > 0);
    public readonly priceAbs: Signal<number> = computed<number>(() => this.price() ? Math.abs(this.price()) : this.guestPrice() ? 0 : undefined);
    public readonly priceSign: Signal<string> = computed<string>(() => !this.price() ? "" : this.price() < 0 ? "-" : this.showPriceSign() ? "+" : "");
    public readonly guestPriceAbs: Signal<number> = computed<number>(() => this.guestPrice() ? Math.abs(this.guestPrice()) : undefined);
    public readonly guestPriceSign: Signal<string> = computed<string>(() => !this.guestPrice() ? "" : this.guestPrice() < 0 ? "-" : this.showPriceSign() ? "+" : "");

    optionSelected(event: MouseEvent): void {
        if (this.disableControl() || (this.controlType() === SelectorType.RADIO && this.selected())) {
            event.preventDefault();
            return;
        }
        this.onSelect.emit();
    }
}
